<script lang="ts">
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type {Field, RelatedListButtonType, Customer, CustomerType, AuditLog, SectionLinkType} from "@/types/types";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import Debug from "@/components/Debug.svelte";
    import { router, useForm } from '@inertiajs/svelte';
    import InlineEditable from "@/components/Inputs/InlineEditable.svelte";

    import FormBlock from "@/components/Form/FormBlock.svelte";
    import AppLayout from "@/layouts/AppLayout.svelte";
    import {Pencil, Plus, Trash, Building2, History, FileText, MessageSquare, Briefcase, CircleUser} from "lucide-svelte";
    import AuditLogComponent from '@/components/AuditLogComponent.svelte';
    import {Accordion, AccordionContent, AccordionItem, AccordionTrigger,} from "@/components/ui/accordion"
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import EditCustomerModal from "@/components/customers/EditCustomerModal.svelte";
    import {Button} from "@/components/ui/button";
    import CreateDocumentModal from "@/components/documents/CreateDocumentModal.svelte";
    import RelatedDocuments from "@/components/documents/RelatedDocuments.svelte";
    import type {Document} from "@/types/types";
    import * as Card from '@/components/ui/card';
    import CreateSiteModal from "@/components/sites/CreateSiteModal.svelte";
    import CreateJobModal from "@/components/jobs/CreateJobModal.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import LeadTable from "@/components/leads/LeadTable.svelte";
    import LinkExistingModal from "@/components/LinkExistingModal.svelte";
    import {Link} from "lucide-svelte";
    import Comments from '@/components/Comments.svelte';
    import { page } from '@inertiajs/svelte';
    import type { Comment } from "@/types/types";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import CreateCustomerModal from "@/components/customers/CreateCustomerModal.svelte";
    import LinkContactsModal from "@/components/customers/LinkContactsModal.svelte";

    type Props = {
        customer: Customer;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        relatedDocuments?: Document[];
        jobsBlocks: (Blocks & { fields: Field[] })[];
        comments: Comment[];
        customerTypeOptions?: Record<string, string>;
        availableOrganisations?: { id: string; name: string }[];
        entityFieldColumn?: string;
        auditLogs?: AuditLog[];
    };

    let {customer, blocks, documentBlocks, relatedDocuments, jobsBlocks, comments, customerTypeOptions = {}, availableOrganisations = [], entityFieldColumn, auditLogs = []}: Props = $props();

    // Section links for quick navigation
    const sectionLinks: SectionLinkType[] = [
        { id: 'section-contacts', label: 'Contacts', icon: CircleUser },
        { id: 'section-jobs', label: 'Jobs', icon: Briefcase },
        { id: 'section-leads', label: 'Leads', icon: CircleUser },
        { id: 'section-documents', label: 'Documents', icon: FileText },
        { id: 'section-comments', label: 'Comments', icon: MessageSquare },
        { id: 'section-audit-log', label: 'History', icon: History },
    ];

    const isOrganisation = $derived(customer.type === 'organisation');
    const isContact = $derived(customer.type === 'contact');

    // Filter fields based on customer type
    // - Organisation: hide organisation_id field (self-referential doesn't make sense)
    // - Contact: hide organisation_name field (use the related organisation instead)
    function shouldShowField(field: Field): boolean {
        if (isOrganisation && field.column_name === 'organisation_id') return false;
        if (isContact && field.column_name === 'organisation_name') return false;
        return true;
    }
    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (customer as Record<string, any>)[entityFieldColumn]
        ? (customer as Record<string, any>)[entityFieldColumn]
        : 'Customer Details';

    let breadcrumbs = $state([
        {title: 'Customers', href: route('customers.index')},
        {title: entityDisplayName, href: route('customers.show', {customer: customer.id})},
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        {
            title: 'Edit Customer',
            href: undefined,
            onClick: openEditCustomerModal,
            icon: Pencil,
        },
        {
            title: 'Add New Customer',
            href: undefined,
            icon: Plus,
            onClick: openCreateCustomerModal,
        },
        {
            title: 'Delete Customer',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('customers.destroy', {customer: customer.id}));
            },
            icon: Trash,
        }
    ]);

    let showCreateCustomerModal = $state(false);

    function openCreateCustomerModal() {
        showCreateCustomerModal = true;
    }

    let editCustomerModal = $state(false);

    function openEditCustomerModal() {
        editCustomerModal = true;
    }


    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

    let showCreateJobModal = $state(false);
    function openCreateJobModal(){
        showCreateJobModal = true;
    }

    let showLinkJobModal = $state(false);
    function openLinkJobModal() {
        showLinkJobModal = true;
    }

    let inlineFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }

    // Fix related_jobs to be just IDs if it's an array of objects
    if (customer.related_jobs && Array.isArray(customer.related_jobs)) {
        inlineFormInner['related_jobs'] = customer.related_jobs.map(j => j.id);
    }

    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);

    // Contact modal for adding contacts to organisation
    let showAddContactModal = $state(false);
    function openAddContactModal() {
        showAddContactModal = true;
    }

    let showLinkContactModal = $state(false);
    function openLinkContactModal() {
        showLinkContactModal = true;
    }

</script>
<!--<Debug {formInner} {form} {$form}/>-->

<svelte:head>
    <title>Customer - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons} {sectionLinks}>
    <!-- Organisation Info (for contacts) -->
    {#if isContact && customer.organisation}
        <div class="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 class="h-4 w-4" />
            <span>Part of</span>
            <a
                href={route('customers.show', { customer: customer.organisation.id })}
                class="font-medium text-primary hover:underline"
            >
                {customer.organisation.name}
            </a>
        </div>
    {/if}

    <FormContainer>
        {#each blocks as block}
            <FormBlock label={block.label}>
                {#each block.fields as field}
                    {#if shouldShowField(field)}
                        <InlineEditable
                            field={field}
                            bind:editingFieldId={editingFieldId}
                            error={$inlineForm.errors[field.column_name]}
                            onSave={(newValue) => {
                                    $inlineForm[field.column_name] = newValue;
                                    $inlineForm.put(route("customers.update", { customer }), {
                                        onSuccess: () => {
                                           editingFieldId = undefined;
                                           router.visit(window.location.href, {
                                                only: ['customer']
                                            });
                                        },
                                        onError: () => {

                                        }
                                    });
                                    // editingFieldId = undefined;
                                }
                            }
                            record={customer}
                        />
                    {/if}
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>

    <!-- Contacts Section (only for Organisations) -->
    {#if isOrganisation}
        {#snippet contactsTableHeader()}
            <div class="flex gap-2">
                <Button
                    onclick={openLinkContactModal}
                    variant="outline"
                    size="sm"
                    class="flex items-center gap-2"
                >
                    <Link class="h-4 w-4" /> Link Existing
                </Button>
                <Button
                    onclick={openAddContactModal}
                    size="sm"
                    class="flex items-center gap-2"
                >
                    <Plus class="h-4 w-4" /> Add Contact
                </Button>
            </div>
        {/snippet}
        <CustomerTable
            view="relatedList"
            relatedData={{
                column: 'organisation_id',
                record_id: customer.id
            }}
            hidePrimaryColumn={true}
            title="Related Contacts"
            headerSnippet={contactsTableHeader}

        />
    {/if}

    {#snippet jobTableHeader()}
        <div class="flex gap-2">
                <Button
                    onclick={openLinkJobModal}
                    class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent ">
                    <Link class="h-4 w-4"/> Link Existing
                </Button>
                <Button
                    onclick={openCreateJobModal}
                    class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent ">
                    <Plus class="h-4 w-4"/> Add Job
                </Button>
        </div>
    {/snippet}
    <JobTable
        view="relatedList"
        relatedData={{
                        column : 'customers',
                        record_id : customer.id
                    }}
        title="Related Jobs"
        headerSnippet={jobTableHeader}
    />
    <CreateJobModal
        blocks={jobsBlocks}
        bind:show={showCreateJobModal}
        relatedRecordId={customer.id}
        relatedRecordLabel={customer.name}
    />

    <LinkExistingModal
        bind:show={showLinkJobModal}
        entityTitle="Jobs"
        TableComponent={JobTable}
        relatedRecordId={customer.id}
        relationshipName="related_jobs"
        syncRoute="customers.update"
        currentlyRelated={customer.related_jobs}
        recordData={inlineFormInner}
        routeParamName="customer"
    />

    <LeadTable
        view="relatedList"
        relatedData={{
            column: 'customers',
            record_id: customer.id
        }}
        title="Related Leads"
    />

<!--    <Accordion type="single" collapsible>-->
<!--        <AccordionItem value="item-1">-->
<!--            <AccordionTrigger>-->
<!--               -->
<!--            </AccordionTrigger>-->

<!--            <AccordionContent>-->
<!--               -->
<!--            </AccordionContent>-->
<!--        </AccordionItem>-->
<!--    </Accordion>-->

    <RelatedDocuments {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Customer"
            relatedRecordId={customer.id}
            relatedRecordLabel={customer.name}
        />
    </RelatedDocuments>

    <Comments
        id="section-comments"
        commentableId={customer.id}
        commentableType="App\Models\Customer"
        {comments}
        currentUser={$page.props.auth.user}
    />

    <AuditLogComponent id="section-audit-log" {auditLogs} />

    <CreateCustomerModal
        bind:show={showCreateCustomerModal}
        {blocks}
        {customerTypeOptions}
        {availableOrganisations}
    />

    <!-- Modal for adding a new contact to this organisation -->
    {#if isOrganisation}
        <CreateCustomerModal
            bind:show={showAddContactModal}
            {blocks}
            {customerTypeOptions}
            {availableOrganisations}
            defaultType="contact"
            defaultOrganisationId={customer.id}
        />

        <LinkContactsModal
            bind:show={showLinkContactModal}
            organisationId={customer.id}
            currentContacts={customer.contacts ?? []}
        />
    {/if}

    <EditCustomerModal
        blocks={blocks}
        bind:show={editCustomerModal}
        customer={customer}
        {customerTypeOptions}
        {availableOrganisations}
    />

</AppLayout>
